import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  employeeImportErrorMessage,
  useCommitEmployeeImport,
  useDownloadEmployeeImportTemplate,
  usePreviewEmployeeImport,
} from "@/hooks/useOrgEmployeeImport";
import type {
  EmployeeImportPreviewResponse,
  EmployeeImportResultResponse,
} from "@/types/generated/cms.generated";
import { CheckCircle2, Download } from "lucide-react";

interface EmployeeImportDialogProps {
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Server limit: ≤ 5 MB. Checked client-side too (as in the client portal) so a huge file is never
 * uploaded — above the limit the server answers 413 without an envelope. */
const MAX_FILE_BYTES = 5 * 1024 * 1024;

type RowAction = "new" | "update" | "unchanged" | "error";

const ACTION_LABELS: Record<RowAction, string> = {
  new: "New",
  update: "Update",
  unchanged: "Unchanged",
  error: "Error",
};

const ACTION_VARIANTS: Record<RowAction, "default" | "secondary" | "outline" | "destructive"> = {
  new: "default",
  update: "secondary",
  unchanged: "outline",
  error: "destructive",
};

function plural(n: number, singular: string, pluralForm = `${singular}s`) {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

function asAction(action: string | null | undefined): RowAction {
  const a = (action ?? "").toLowerCase();
  return a === "new" || a === "update" || a === "unchanged" ? a : "error";
}

/**
 * Employee CSV import on the client's behalf: template → choose file → preview
 * (new / update / unchanged / error + seat impact) → Import (only with zero errors) → result.
 * The commit is atomic server-side; the preview is advisory, the server re-validates.
 */
export function EmployeeImportDialog({ organizationId, open, onOpenChange }: EmployeeImportDialogProps) {
  const downloadTemplate = useDownloadEmployeeImportTemplate(organizationId);
  const previewImport = usePreviewEmployeeImport(organizationId);
  const commitImport = useCommitEmployeeImport(organizationId);

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<EmployeeImportPreviewResponse | null>(null);
  const [result, setResult] = useState<EmployeeImportResultResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createMissingOrgUnits, setCreateMissingOrgUnits] = useState(false);
  // Synchronous double-submit guard: `isPending` only flips on the next render, so a fast
  // double-click could otherwise fire two commits.
  const committingRef = useRef(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setCreateMissingOrgUnits(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFile = (chosen: File | null) => {
    setFile(chosen);
    setPreview(null);
    setResult(null);
    setError(null);
    if (!chosen) return;
    if (chosen.size > MAX_FILE_BYTES) {
      setFile(null);
      setError("The file is larger than 5 MB. Split it into smaller files and import them one at a time.");
      return;
    }
    previewImport.mutate(chosen, {
      onSuccess: (data) => setPreview(data),
      onError: (err: unknown) => setError(employeeImportErrorMessage(err)),
    });
  };

  const handleImport = () => {
    if (!file || committingRef.current || commitImport.isPending) return;
    committingRef.current = true;
    setError(null);
    commitImport.mutate(
      { file, createMissingOrgUnits },
      {
        onSuccess: (data) => {
          committingRef.current = false;
          setResult(data ?? {});
        },
        onError: (err: unknown) => {
          committingRef.current = false;
          setError(employeeImportErrorMessage(err));
        },
      },
    );
  };

  const rows = preview?.rows ?? [];
  const totals = preview?.totals;
  const errorCount = Math.max(
    totals?.error ?? 0,
    rows.filter((r) => asAction(r.action) === "error").length,
  );
  const seat = preview?.seatImpact;
  const seatsShort = !!seat && (seat.seatsRequired ?? 0) > (seat.seatsAvailable ?? 0);
  const missing = preview?.missingUnits;
  const missingList = [
    ...(missing?.companies ?? []).map((n) => `Company: ${n}`),
    ...(missing?.departments ?? []).map((n) => `Department: ${n}`),
    ...(missing?.locations ?? []).map((n) => `Location: ${n}`),
  ];
  // The server refuses the whole import (nothing is saved) when names don't match and
  // create-missing is off, or when there aren't enough seats — so Import is blocked up front.
  const missingBlocksImport = missingList.length > 0 && !createMissingOrgUnits;
  const canImport =
    !!file &&
    !!preview &&
    errorCount === 0 &&
    !missingBlocksImport &&
    !seatsShort &&
    !commitImport.isPending &&
    !previewImport.isPending &&
    !result;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="employee-import-dialog">
        <DialogHeader>
          <DialogTitle>Import employees</DialogTitle>
          <DialogDescription>
            Download the template, fill it in, then choose the file to preview the changes before
            importing. Nothing is saved until you press Import.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3" data-testid="employee-import-result">
            <p className="flex items-center gap-2 font-medium">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              Import complete
            </p>
            <ul className="text-sm space-y-1">
              <li>{result.created ?? 0} created</li>
              <li>{result.updated ?? 0} updated</li>
              <li>{result.unchanged ?? 0} unchanged</li>
              <li>{plural(result.seatsAssigned ?? 0, "seat")} assigned</li>
              <li>
                New org units: {plural(result.createdCompanies ?? 0, "company", "companies")},{" "}
                {plural(result.createdDepartments ?? 0, "department")},{" "}
                {plural(result.createdLocations ?? 0, "location")}
              </li>
            </ul>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => downloadTemplate.mutate()}
                disabled={downloadTemplate.isPending}
              >
                <Download className="h-4 w-4 mr-2" />
                Download template
              </Button>
              <div className="flex-1 min-w-[220px] space-y-1">
                <Label htmlFor="employee-import-file">CSV file</Label>
                <Input
                  id="employee-import-file"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const chosen = e.target.files?.[0] ?? null;
                    // Clear the input so choosing the same (fixed) file again fires `change` again.
                    e.target.value = "";
                    handleFile(chosen);
                  }}
                  disabled={commitImport.isPending}
                />
              </div>
            </div>

            {previewImport.isPending && (
              <p className="text-sm text-muted-foreground">Checking the file…</p>
            )}

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            {preview && (
              <>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm" data-testid="employee-import-totals">
                  <span>{totals?.new ?? 0} new</span>
                  <span>{plural(totals?.update ?? 0, "update")}</span>
                  <span>{totals?.unchanged ?? 0} unchanged</span>
                  <span className={errorCount > 0 ? "text-destructive font-medium" : undefined}>
                    {plural(errorCount, "error")}
                  </span>
                </div>

                <div
                  className={`text-sm rounded-md border p-3 ${seatsShort ? "border-destructive text-destructive" : ""}`}
                  data-testid="employee-import-seat-impact"
                >
                  {plural(seat?.seatsRequired ?? 0, "seat")} needed · {seat?.seatsAvailable ?? 0} available
                  ({seat?.assignedSeats ?? 0} of {seat?.purchasedSeats ?? 0} assigned)
                  {seatsShort && (
                    <span className="block mt-1">
                      Not enough seats — the whole import will be refused — nothing is imported. Add
                      seats, or set assign_seat to no on some rows, then choose the file again.
                    </span>
                  )}
                </div>

                <div className="max-h-72 overflow-y-auto rounded-md border">
                  <Table data-testid="employee-import-preview-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Line</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="w-28">Result</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((row, i) => {
                        const action = asAction(row.action);
                        return (
                          <TableRow key={`${row.line ?? i}-${row.email ?? ""}`}>
                            <TableCell>{row.line}</TableCell>
                            <TableCell className="font-mono text-xs">{row.email}</TableCell>
                            <TableCell>
                              <Badge variant={ACTION_VARIANTS[action]}>{ACTION_LABELS[action]}</Badge>
                            </TableCell>
                            <TableCell className="text-xs">
                              {(row.messages ?? []).map((m) => (
                                <span key={m} className="block">
                                  {m}
                                </span>
                              ))}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {errorCount > 0 && (
                  <p className="text-sm text-destructive">
                    Fix the errors in your file, then choose it again. Import is available once
                    there are no errors.
                  </p>
                )}

                <div className="space-y-2">
                  {missingList.length > 0 && (
                    <div className="text-sm" data-testid="employee-import-missing-units">
                      <p className="text-muted-foreground">Not found in this organization:</p>
                      <ul className="list-disc pl-5">
                        {missingList.map((m) => (
                          <li key={m}>{m}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="employee-import-create-missing"
                      checked={createMissingOrgUnits}
                      onCheckedChange={(checked) => setCreateMissingOrgUnits(checked === true)}
                    />
                    <Label htmlFor="employee-import-create-missing" className="font-normal">
                      Create missing companies/departments/locations
                    </Label>
                  </div>
                  {missingBlocksImport && (
                    <p className="text-sm text-destructive" data-testid="employee-import-blocked-reason">
                      Tick &apos;Create missing companies/departments/locations&apos; or fix the names in
                      the file.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button type="button" onClick={() => handleOpenChange(false)}>
              Done
            </Button>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleImport}
                disabled={!canImport}
                data-testid="employee-import-submit"
              >
                {commitImport.isPending ? "Importing..." : "Import"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
